/**
 * Classify programs — propose labels, human-confirm before save.
 * Labels ≠ membership. Attach is a separate step (ws_group_member).
 *
 * Dry-run (previewOnly): review + filter + select; Apply is optional;
 * can go straight to Attach for already-labelled cohorts.
 * Classify (applyFlow): same review; Apply labels is the primary write.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import {
  classifyUniqueUserPrograms,
  ClassifyUniqueUserProgramsResponse,
  ClassifyPreviewRow,
} from '../../services/api';
import { UniqueUser } from '../../types';

const DEFAULT_LIMIT = 100;
const MAX_LIMIT = 500;

type Props = {
  open: boolean;
  onClose: () => void;
  selectedUsers: UniqueUser[];
  intent: 'previewOnly' | 'applyFlow';
  onApplied?: () => void | Promise<void>;
  onRequestAttach?: (users: {
    id: number | string;
    userId: string;
    email?: string | null;
    programCode?: string | null;
  }[]) => void;
};

function collectUserIds(rows: UniqueUser[]): string[] {
  const ids = new Set<string>();
  for (const row of rows) {
    if (row.userIds?.length) row.userIds.forEach((id) => ids.add(id));
    else if (row.userId) ids.add(row.userId);
  }
  return Array.from(ids);
}

const ClassifyProgramsReviewModal: React.FC<Props> = ({
  open,
  onClose,
  selectedUsers,
  intent,
  onApplied,
  onRequestAttach,
}) => {
  const hasSelection = selectedUsers.length > 0;
  const [step, setStep] = useState<'scope' | 'review'>('scope');
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<ClassifyUniqueUserProgramsResponse | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [includeFilter, setIncludeFilter] = useState('');
  const [excludeFilter, setExcludeFilter] = useState('');
  const [instanceFilter, setInstanceFilter] = useState<string | 'all'>('all');
  const [programFilter, setProgramFilter] = useState<string | 'all'>('all');
  const [changeFilter, setChangeFilter] = useState<'all' | 'new' | 'same'>('all');

  useEffect(() => {
    if (!open) return;
    setLimit(hasSelection ? Math.min(Math.max(selectedUsers.length, 1), MAX_LIMIT) : DEFAULT_LIMIT);
    setStep('scope');
    setResult(null);
    setSelectedRowKeys([]);
    setIncludeFilter('');
    setExcludeFilter('');
    setInstanceFilter('all');
    setProgramFilter('all');
    setChangeFilter('all');
  }, [open, hasSelection, selectedUsers.length]);

  const previewRows = result?.rows || result?.samples || [];

  const instanceOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of previewRows) {
      const id = r.after?.programInstanceId;
      if (id) set.add(id);
    }
    return Array.from(set)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [previewRows]);

  const programOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of previewRows) {
      if (r.after?.programCode) set.add(r.after.programCode);
    }
    return Array.from(set)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [previewRows]);

  const filteredRows = useMemo(() => {
    let rows = previewRows;
    if (instanceFilter !== 'all') {
      rows = rows.filter((r) => r.after?.programInstanceId === instanceFilter);
    }
    if (programFilter !== 'all') {
      rows = rows.filter((r) => r.after?.programCode === programFilter);
    }
    if (changeFilter === 'new') rows = rows.filter((r) => r.changed !== false);
    if (changeFilter === 'same') rows = rows.filter((r) => r.changed === false);
    const include = includeFilter.trim().toLowerCase();
    if (include) {
      rows = rows.filter(
        (r) =>
          r.userId.toLowerCase().includes(include) ||
          (r.email || '').toLowerCase().includes(include) ||
          (r.after?.programInstanceId || '').toLowerCase().includes(include) ||
          (r.after?.programCode || '').toLowerCase().includes(include),
      );
    }
    const exclude = excludeFilter.trim().toLowerCase();
    if (exclude) {
      rows = rows.filter(
        (r) =>
          !(
            r.userId.toLowerCase().includes(exclude) ||
            (r.after?.programInstanceId || '').toLowerCase().includes(exclude)
          ),
      );
    }
    return rows;
  }, [previewRows, includeFilter, excludeFilter, instanceFilter, programFilter, changeFilter]);

  const runPreview = async () => {
    setLoading(true);
    try {
      const userIds = hasSelection ? collectUserIds(selectedUsers) : undefined;
      const data = await classifyUniqueUserPrograms({
        dryRun: true,
        ...(userIds?.length ? { userIds } : {}),
        limit: hasSelection ? Math.min(limit, userIds?.length || limit) : limit,
      });
      setResult(data);
      const rows = data.rows || data.samples || [];
      // Prefer rows that would change; if none (already labelled), select none — operator filters then selects
      const changed = rows.filter((r) => r.changed !== false).map((r) => r.userId);
      setSelectedRowKeys(changed.length > 0 ? changed : []);
      setStep('review');
    } catch (e) {
      message.error((e as Error)?.message || 'Classify preview failed');
    } finally {
      setLoading(false);
    }
  };

  const usersFromKeys = (keys: React.Key[]) => {
    const set = new Set(keys.map(String));
    return previewRows
      .filter((r) => set.has(r.userId))
      .map((r) => ({
        id: r.id,
        userId: r.userId,
        email: r.email,
        programCode: r.after?.programCode,
      }));
  };

  const finishAfterApply = (
    appliedUsers: {
      id: number | string;
      userId: string;
      email?: string | null;
      programCode?: string | null;
    }[],
  ) => {
    if (!onRequestAttach) {
      onClose();
      return;
    }
    Modal.confirm({
      title: 'Labels saved',
      content:
        'Labels (program / instance tag) are not Group membership. Attach selected people to Group Instance(s) next?',
      okText: 'Attach to group…',
      cancelText: 'Done',
      onOk: () => {
        onRequestAttach(appliedUsers);
        onClose();
      },
      onCancel: () => onClose(),
    });
  };

  const handleApply = async () => {
    const userIds = selectedRowKeys.map(String).filter(Boolean);
    if (userIds.length === 0) {
      message.warning('Select at least one row to apply labels');
      return;
    }
    setApplying(true);
    try {
      const data = await classifyUniqueUserPrograms({
        dryRun: false,
        userIds,
        limit: userIds.length,
      });
      message.success(`Applied labels to ${data.updated ?? userIds.length} user(s)`);
      await onApplied?.();
      finishAfterApply(usersFromKeys(userIds));
    } catch (e) {
      message.error((e as Error)?.message || 'Apply classification failed');
    } finally {
      setApplying(false);
    }
  };

  const handleAttachSelected = () => {
    if (!onRequestAttach) return;
    const users = usersFromKeys(selectedRowKeys);
    if (users.length === 0) {
      message.warning('Select rows to attach (filter first if the list is large)');
      return;
    }
    onRequestAttach(users);
    onClose();
  };

  const selectVisible = () => setSelectedRowKeys(filteredRows.map((r) => r.userId));
  const selectVisibleNew = () =>
    setSelectedRowKeys(filteredRows.filter((r) => r.changed !== false).map((r) => r.userId));
  const addVisible = () => {
    const add = filteredRows.map((r) => r.userId);
    setSelectedRowKeys((prev) => Array.from(new Set([...prev.map(String), ...add])));
  };
  const deselectVisible = () => {
    const vis = new Set(filteredRows.map((r) => r.userId));
    setSelectedRowKeys((prev) => prev.filter((k) => !vis.has(String(k))));
  };
  const clearSelection = () => setSelectedRowKeys([]);
  const clearFilters = () => {
    setIncludeFilter('');
    setExcludeFilter('');
    setInstanceFilter('all');
    setProgramFilter('all');
    setChangeFilter('all');
  };

  const summaryBits = useMemo(() => {
    if (!result) return null;
    const codes = Object.entries(result.byCode || {})
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ');
    const instances = Object.entries(result.byInstance || {})
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ');
    return { codes, instances };
  }, [result]);

  const columns = [
    { title: 'User ID', dataIndex: 'userId', key: 'userId', width: 160 },
    { title: 'Email', dataIndex: 'email', key: 'email', ellipsis: true },
    {
      title: 'Program',
      key: 'program',
      width: 110,
      render: (_: unknown, r: ClassifyPreviewRow) => (
        <span>
          {r.before?.programCode || '—'} → <strong>{r.after?.programCode}</strong>
        </span>
      ),
    },
    {
      title: 'Instance tag',
      key: 'instance',
      width: 120,
      render: (_: unknown, r: ClassifyPreviewRow) =>
        r.after?.programInstanceId ? <Tag color="blue">{r.after.programInstanceId}</Tag> : '—',
    },
    {
      title: 'Kind',
      key: 'kind',
      width: 90,
      render: (_: unknown, r: ClassifyPreviewRow) => r.after?.programKind || '—',
    },
    {
      title: 'Residual',
      key: 'residual',
      width: 90,
      render: (_: unknown, r: ClassifyPreviewRow) => r.after?.membershipResidual || '—',
    },
    {
      title: 'Change',
      key: 'changed',
      width: 90,
      render: (_: unknown, r: ClassifyPreviewRow) =>
        r.changed === false ? <Tag>same</Tag> : <Tag color="green">new</Tag>,
    },
  ];

  const isDryRunEntry = intent === 'previewOnly';

  const footer =
    step === 'scope' ? (
      <Space>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="primary" loading={loading} onClick={() => void runPreview()}>
          Run preview
        </Button>
      </Space>
    ) : (
      <Space wrap>
        <Button onClick={() => setStep('scope')}>Back</Button>
        <Button onClick={onClose}>Close</Button>
        <Button
          loading={applying}
          disabled={selectedRowKeys.length === 0}
          onClick={() => void handleApply()}
        >
          Apply labels ({selectedRowKeys.length})
        </Button>
        {onRequestAttach && (
          <Button
            type="primary"
            disabled={selectedRowKeys.length === 0}
            onClick={handleAttachSelected}
          >
            Attach selected… ({selectedRowKeys.length})
          </Button>
        )}
      </Space>
    );

  return (
    <Modal
      title={
        isDryRunEntry
          ? 'Classify preview (labels only — not membership)'
          : 'Classify programs (review & apply labels)'
      }
      open={open}
      onCancel={onClose}
      width={980}
      destroyOnClose
      footer={footer}
    >
      {step === 'scope' && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            This step only proposes <strong>labels</strong> (program / instance tag / residual). It
            does <strong>not</strong> put people in a Group Instance — that is{' '}
            <strong>Attach</strong>. Dry-run / preview never writes until you click Apply labels.
          </Typography.Paragraph>
          {hasSelection ? (
            <Alert
              type="info"
              showIcon
              message={`${selectedUsers.length} user(s) selected — preview uses this selection (capped by limit).`}
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message="No rows selected — preview scans Unique Users in id order up to the limit."
            />
          )}
          <div>
            <Typography.Text strong>Max users to preview</Typography.Text>
            <div style={{ marginTop: 8 }}>
              <InputNumber
                min={1}
                max={MAX_LIMIT}
                value={limit}
                onChange={(v) => setLimit(typeof v === 'number' ? v : DEFAULT_LIMIT)}
              />
              <Typography.Text type="secondary" style={{ marginLeft: 12 }}>
                Default {DEFAULT_LIMIT}; max {MAX_LIMIT}.
              </Typography.Text>
            </div>
          </div>
        </Space>
      )}

      {step === 'review' && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message={
              isDryRunEntry
                ? 'Preview only until you Apply labels. Instance tag suggests which Group Instance to use in Attach — it is not membership. Filter a cohort, select rows, then Attach selected (or Apply labels if tags still need writing).'
                : 'Vet proposed labels, filter a cohort, Apply labels for checked rows, then optionally Attach for membership.'
            }
          />
          {result && (
            <Typography.Text>
              Scanned {result.scanned}; would update {result.updated}
              {result.skippedManual ? `; skipped manual ${result.skippedManual}` : ''}
              {result.truncated ? '; truncated by limit' : ''}
              {summaryBits?.codes ? `; programs: ${summaryBits.codes}` : ''}
              {summaryBits?.instances ? `; instances: ${summaryBits.instances}` : ''}
              {result.updated === 0
                ? ' — nothing new to label (already same); use filters + Attach selected for membership.'
                : ''}
            </Typography.Text>
          )}

          <div>
            <Typography.Text strong>Filter (narrow what you see)</Typography.Text>
            <Space wrap style={{ width: '100%', marginTop: 8 }} size="small">
              <Input
                allowClear
                placeholder="Include: e.g. b25n"
                value={includeFilter}
                onChange={(e) => setIncludeFilter(e.target.value)}
                style={{ width: 160 }}
              />
              <Input
                allowClear
                placeholder="Exclude: e.g. b20n"
                value={excludeFilter}
                onChange={(e) => setExcludeFilter(e.target.value)}
                style={{ width: 160 }}
              />
              <Select
                allowClear
                showSearch
                placeholder="Instance tag"
                style={{ width: 140 }}
                value={instanceFilter === 'all' ? undefined : instanceFilter}
                options={instanceOptions}
                onChange={(v) => setInstanceFilter(v == null ? 'all' : v)}
              />
              <Select
                allowClear
                showSearch
                placeholder="Program"
                style={{ width: 120 }}
                value={programFilter === 'all' ? undefined : programFilter}
                options={programOptions}
                onChange={(v) => setProgramFilter(v == null ? 'all' : v)}
              />
              <Select
                value={changeFilter}
                style={{ width: 130 }}
                onChange={(v) => setChangeFilter(v)}
                options={[
                  { value: 'all', label: 'Change: all' },
                  { value: 'new', label: 'New only' },
                  { value: 'same', label: 'Same only' },
                ]}
              />
              <Typography.Link onClick={clearFilters}>Clear filters</Typography.Link>
            </Space>
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              Showing {filteredRows.length} of {previewRows.length}
              {selectedRowKeys.length ? ` · ${selectedRowKeys.length} selected` : ''}
            </Typography.Text>
          </div>

          <div>
            <Typography.Text strong>Selection (visible rows)</Typography.Text>
            <Space wrap style={{ display: 'block', marginTop: 6 }}>
              <Typography.Link onClick={selectVisible}>Select all visible</Typography.Link>
              <Typography.Text type="secondary"> · </Typography.Text>
              <Typography.Link onClick={selectVisibleNew}>Select visible new</Typography.Link>
              <Typography.Text type="secondary"> · </Typography.Text>
              <Typography.Link onClick={addVisible}>Add visible</Typography.Link>
              <Typography.Text type="secondary"> · </Typography.Text>
              <Typography.Link onClick={deselectVisible}>Deselect visible</Typography.Link>
              <Typography.Text type="secondary"> · </Typography.Text>
              <Typography.Link onClick={clearSelection}>Clear selection</Typography.Link>
            </Space>
          </div>

          <Table
            className="classify-review-table"
            size="small"
            rowKey={(r) => r.userId}
            pagination={{ pageSize: 20 }}
            scroll={{ y: 360 }}
            columns={columns as any}
            dataSource={filteredRows}
            locale={{
              emptyText: includeFilter || excludeFilter || instanceFilter !== 'all'
                ? 'No rows match this filter'
                : 'No preview rows',
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
              preserveSelectedRowKeys: true,
            }}
          />
          <style>{`
            .classify-review-table .ant-checkbox-inner {
              border: 2px solid #5A6474 !important;
              background: #fff !important;
            }
            .classify-review-table .ant-checkbox-checked .ant-checkbox-inner {
              background: #0B5FD0 !important;
              border-color: #0B5FD0 !important;
            }
          `}</style>
        </Space>
      )}
    </Modal>
  );
};

export default ClassifyProgramsReviewModal;
